/**
 * Kind-aware master property-code search for the APAC review page's dropdowns.
 *
 * APAC keeps three kinds of master code (entity, asset, investment) in one table.
 * A line's mapped entity, mapped asset and mapped investment each draw from their
 * own kind, so a dropdown must search only that kind: an entity picked as an asset
 * would pass the UI and only surface at output.
 */

import {
    searchMasterPropertyCodes as searchMasterCodes,
    type PropertyCodeSearchApi,
} from "@/useCases/masterPropertyCodes";
import type { TSearchableOption } from "@/ui/molecules";

/** The three kinds of master property code APAC distinguishes. */
export type TPropertyCodeKind = "entity" | "asset" | "investment";

/** The slice of `api.propertyCodes` the kind-aware search needs. */
export type TKindedPropertyCodeSearchApi = {
    listPaginated: (params?: {
        search?: string;
        page?: number;
        page_size?: number;
        active_only?: boolean;
        kind?: string;
    }) => Promise<{ items: Array<{ code: string; description: string | null }> }>;
};

/**
 * Search the company's active master codes of one `kind`, server-side, matching
 * code OR name. Query handling, page size and `CODE - Description` labels are the
 * shared master-code search's; this only scopes every request to `kind`.
 */
export function searchMasterPropertyCodes(
    api: TKindedPropertyCodeSearchApi,
    query: string,
    kind: TPropertyCodeKind,
): Promise<TSearchableOption[]> {
    const scoped: PropertyCodeSearchApi = {
        listPaginated: (params) => api.listPaginated({ ...params, kind }),
    };
    return searchMasterCodes(scoped, query);
}
